import React, { useEffect, useState } from 'react';
import { useEntityList } from '@/hooks/useEntityList';
import { exportEntityCsv } from '@/lib/csv';
import { getSession } from '../../lib/auth-session';
import { useUser } from '@/contexts/UserContext';
import { getApiUrl } from '@/lib/api-config';
import { SectionLayout } from '@/components/section-layout';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Download, Upload } from 'lucide-react';
import { GenericTag } from '@/styles/GenericTag';
import { getProjectTypeLabel } from '@/lib/project-types';
import { trackProjectCreated, trackProjectUpdated, trackFormSubmission, trackButtonClick } from '@/lib/posthog-events';
import { ProjectForm, type ProjectFormData } from './info/ProjectForm';
import { saveProjectAndMembers } from './info/projectAccessApi';
import { uploadProjectAttachments, fetchProjectAttachments, type ProjectFileAttachment } from './store';
import { mapStatusToTag } from './status-tag';

interface Project {
  id: string;
  name: string;
  description: string;
  status: string;
  budget: number;
  start_date: string;
  end_date: string;
  created_at: string;
  company_id: string;
  created_by: string;
  client_name: string;
  client_id: string | null;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  latitude: number | null;
  longitude: number | null;
  project_type: string;
  square_footage: number;
  assignee_id: string | null;
}

/** The fields a project's update sends: what the form submits and what the stored project holds */
type ProjectFields = Pick<ProjectFormData, 'name' | 'client_id' | 'client_name' | 'address' | 'city' | 'state' | 'country' | 'zip_code' | 'latitude' | 'longitude' | 'start_date' | 'end_date' | 'budget' | 'description' | 'status' | 'project_type' | 'square_footage' | 'assignee_id'>;

interface ProjectStats {
  total_tasks: number;
  completed_tasks: number;
  budget_spent: number;
}

// What the table shows besides the project's own columns: spent so far (the server sums each project's expenses for the page).
// The Tasks counters have no source yet (they always read 0/0, as before this list became server-driven).
const statsOf = (project: object): ProjectStats => ({ budget_spent: Number((project as { budget_spent?: number }).budget_spent ?? 0), total_tasks: 0, completed_tasks: 0 });

export function ProjectPage() {
  const { companyId, user } = useUser();
  // The list: ONE server-driven query (search + sort + page) over the company's projects -- the database searches, sorts and pages them,
  // and sums each project's expenses (hooks/useEntityList.ts); no per-project stats query.
  const list = useEntityList<any, Project & { budget_spent?: number }>({
    entityType: 'project',
    enabled: !!companyId,
    mode: 'pages',
    includeRollups: true,
    defaultSort: { columnKey: 'name', direction: 'asc', sortKey: 'name' },
    mapRow: (row) => row,
  });
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [projectAttachments, setProjectAttachments] = useState<ProjectFileAttachment[]>([]);

  // Session / load problems end up in the same message slot the page already had
  useEffect(() => {
    if (companyId && !getSession()) setError('Please log in to access this feature');
  }, [companyId]);
  useEffect(() => {
    if (list.error) setError('Error loading projects. Please try again.');
  }, [list.error]);

  // Listen for projects updated events to refresh the list
  const { reload } = list;
  useEffect(() => {
    const handleProjectsUpdated = () => {
      if (companyId) reload();
    };

    window.addEventListener('projectsUpdated', handleProjectsUpdated);
    return () => {
      window.removeEventListener('projectsUpdated', handleProjectsUpdated);
    };
  }, [companyId, reload]);

  const handleAddProject = () => {
    trackButtonClick('new_project_button', { context: 'project_list' });
    setCurrentProject(null);
    setProjectAttachments([]);
    setError(null);
    setIsFormOpen(true);
  };

  const handleRowClick = async (project: Project) => {
    trackButtonClick('edit_project_button', {
      context: 'project_list',
      project_id: project.id,
      project_name: project.name
    });
    setCurrentProject(project);
    setError(null);
    setIsFormOpen(true);
    if (companyId) {
      const attachments = await fetchProjectAttachments(project.id);
      setProjectAttachments(attachments);
    }
  };

  const parseErrorMessage = (error: any): string => {
    if (typeof error === 'string') {
      return error;
    }

    const errorMessage = error?.message || error?.error || '';
    const errorDetails = error?.details || error?.error?.details || '';
    const errorCode = error?.code || error?.error?.code || '';

    if (errorCode === '23502') {
      const fieldMatch = errorDetails?.match(/column "(\w+)" of relation/);
      if (fieldMatch) {
        const fieldName = fieldMatch[1];
        const friendlyName = fieldName
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase());
        return `${friendlyName} is required.`;
      }
      return 'A required field is missing. Please check all required fields.';
    }

    if (errorCode === '23503') {
      return 'Invalid reference. Please check that all related records exist.';
    }

    if (errorCode === '23514') {
      if (errorMessage.includes('status')) {
        return 'Invalid status value. Please select a valid status.';
      }
      return 'Invalid data format. Please check your input.';
    }

    if (errorMessage.includes('user_id and company_id are required')) {
      return 'Session expired. Please refresh the page and try again.';
    }

    if (errorMessage.includes('Failed to create project') || errorMessage.includes('Failed to update project')) {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('required') || detailsStr.includes('not null')) {
        return 'Failed to save project. Please check all required fields and try again.';
      }
      return 'Failed to save project. Please check all fields and try again.';
    }

    return errorMessage || 'An unexpected error occurred. Please try again.';
  };

  const handleFormSubmit = async (data: ProjectFormData) => {
    setError(null);
    try {
      const session = getSession();
      if (!session) {
        throw new Error('No active session');
      }

      if (currentProject) {
        // Update existing project via generic API
        const putProject = async (values: ProjectFields) => {
          const response = await fetch(getApiUrl(`api/entities/project/${currentProject.id}`), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: session.user.id,
              company_id: currentProject.company_id,
              name: values.name,
              client_id: values.client_id,
              client_name: values.client_name,
              address: values.address,
              city: values.city,
              state: values.state,
              country: values.country,
              zip_code: values.zip_code,
              latitude: values.latitude,
              longitude: values.longitude,
              start_date: values.start_date,
              end_date: values.end_date,
              budget: values.budget,
              description: values.description,
              status: values.status,
              project_type: values.project_type,
              square_footage: values.square_footage,
              assignee_id: values.assignee_id || null,
              updated_at: new Date().toISOString()
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(parseErrorMessage(errorData));
          }
        };

        // The project's fields and its members are one Save Changes: when the members are refused the fields go back to what they were
        await saveProjectAndMembers({
          projectId: currentProject.id,
          members: data.members,
          save: () => putProject(data),
          undo: () => putProject(currentProject),
        });

        trackProjectUpdated({
          project_id: currentProject.id,
          budget: data.budget,
          project_type: data.project_type,
          project_name: data.name,
          client_name: data.client_name,
          square_footage: data.square_footage,
          status: data.status,
          start_date: data.start_date,
          end_date: data.end_date,
          city: data.city,
          state: data.state,
          company_id: currentProject.company_id,
          updated_by: session.user.id
        });

        trackFormSubmission({ form_type: 'project_update', success: true });
      } else {
        // The company comes from the session (the server re-derives it)
        if (!companyId) throw new Error('User has no associated company');

        const response = await fetch(getApiUrl('api/entities/project'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: session.user.id,
            company_id: companyId,
            name: data.name,
            client_id: data.client_id,
            members: data.members,
            client_name: data.client_name,
            address: data.address,
            city: data.city,
            state: data.state,
            country: data.country,
            zip_code: data.zip_code,
            latitude: data.latitude,
            longitude: data.longitude,
            start_date: data.start_date,
            end_date: data.end_date,
            budget: data.budget,
            description: data.description,
            status: data.status,
            project_type: data.project_type,
            square_footage: data.square_footage,
            assignee_id: data.assignee_id || null,
            created_by: session.user.id,
            created_at: new Date().toISOString()
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(parseErrorMessage(errorData));
        }

        const result = await response.json();
        const createdProject = result.data;

        // Upload any attachments picked before the project existed --
        // same "create record, then attach files to the new id" pattern
        // as Create Lead.
        if (Array.isArray(data.attachmentsFiles) && data.attachmentsFiles.length > 0 && createdProject?.id) {
          try {
            await uploadProjectAttachments(createdProject.id, data.attachmentsFiles, companyId, session.user.id);
          } catch (uploadError) {
            console.error('Error uploading attachments after create:', uploadError);
          }
        }

        trackProjectCreated({
          project_id: createdProject.id,
          budget: createdProject.budget,
          project_type: createdProject.project_type,
          project_name: createdProject.name,
          client_name: createdProject.client_name,
          square_footage: createdProject.square_footage,
          status: createdProject.status,
          start_date: createdProject.start_date,
          end_date: createdProject.end_date,
          city: createdProject.city,
          state: createdProject.state,
          company_id: companyId,
          created_by: session.user.id
        });

        trackFormSubmission({ form_type: 'project_creation', success: true });
      }

      list.reload();
      setIsFormOpen(false);
      setCurrentProject(null);
      setError(null);

      // Trigger a custom event to refresh other components
      window.dispatchEvent(new CustomEvent('projectsUpdated'));
    } catch (err) {
      console.error('Error saving project:', err);
      const userFriendlyError = parseErrorMessage(err);
      trackFormSubmission({
        form_type: currentProject ? 'project_update' : 'project_creation',
        success: false,
        error_message: userFriendlyError
      });
      setError(userFriendlyError);
    }
  };

  const handleCancelForm = () => {
    setIsFormOpen(false);
    setCurrentProject(null);
    setProjectAttachments([]);
    setError(null);
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one project to delete.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} project(s)? This action cannot be undone.`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete projects.');
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const projectId of selectedRows) {
      try {
        // Use generic API to hard delete the project
        const response = await fetch(getApiUrl(`api/entities/project/${projectId}`), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.id,
            company_id: companyId
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete project');
        }

        successCount++;
      } catch (error) {
        console.error('Error deleting project:', error);
        errorCount++;
      }
    }

    // Refresh the projects list
    list.reload();
    setSelectedRows([]);

    // Trigger a custom event to refresh other components
    window.dispatchEvent(new CustomEvent('projectsUpdated'));

    if (errorCount > 0) {
      alert(`Deleted ${successCount} project(s). ${errorCount} failed.`);
    } else {
      alert(`Successfully deleted ${successCount} project(s).`);
    }
  };

  // Export to CSV: the projects selected in the table, or -- with nothing selected -- EVERY project the current search matches,
  // read by the backend (not only the page that is showing).
  const handleExportSelected = () => exportEntityCsv(list.exportRows, selectedRows, 'projects', 'projects');

  const columns: GenericTableColumn<Project>[] = [
    {
      key: 'name',
      header: 'Project Name',
      width: '11.25rem',
      sortable: true,
      sortKey: 'name',
    },
    {
      key: 'client_name',
      header: 'Client',
      width: '10rem',
      sortable: true,
      sortKey: 'client_name',
    },
    {
      key: 'city',
      header: 'Location',
      width: '9rem',
      sortable: true,
      sortKey: 'city',
      render: (row) => `${row.city || ''}, ${row.state || ''}`,
    },
    {
      key: 'project_type',
      header: 'Type',
      width: '8rem',
      sortable: true,
      sortKey: 'project_type',
      render: (row) => getProjectTypeLabel(row.project_type),
    },
    {
      key: 'status',
      header: 'Status',
      width: '7.5rem',
      sortable: true,
      sortKey: 'status',
      render: (row) => (
        <GenericTag
          type="status"
          value={mapStatusToTag(row.status)}
        />
      ),
    },
    {
      key: 'tasks',
      header: 'Tasks',
      width: '6rem',
      render: (row) => {
        const stats = statsOf(row);
        return `${stats.completed_tasks}/${stats.total_tasks}`;
      },
    },
    {
      key: 'budget',
      header: 'Budget',
      width: '10rem',
      sortable: true,
      sortKey: 'budget',
      render: (row) => {
        const stats = statsOf(row);
        const percent = row.budget ? (stats.budget_spent / row.budget) * 100 : 0;
        return (
          <div>
            <div>${stats.budget_spent.toLocaleString()} / ${(row.budget || 0).toLocaleString()}</div>
            <div className="text-muted">{percent.toFixed(1)}% spent</div>
          </div>
        );
      },
    },
    {
      key: 'start_date',
      header: 'Timeline',
      width: '10rem',
      sortable: true,
      sortKey: 'start_date',
      render: (row) => {
        const days = Math.ceil((new Date(row.end_date).getTime() - new Date(row.start_date).getTime()) / (1000 * 60 * 60 * 24));
        return (
          <div>
            <div>{new Date(row.start_date).toLocaleDateString()} - {new Date(row.end_date).toLocaleDateString()}</div>
            <div className="text-muted">{days} days</div>
          </div>
        );
      },
    },
  ];

  const menuItems = [
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => console.log('Import clicked'),
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExportSelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
  ];

  if (error && list.items.length === 0 && list.hasLoaded) {
    return (
      <SectionLayout title="Projects">
        <div className="space-y-6">
          <div className="flex items-center justify-end">
            <div className="flex items-center gap-2">
              <Button onClick={handleAddProject} className="flex items-center gap-2">
                <Plus size={16} />
                New Project
              </Button>
            </div>
          </div>
          <div className="text-center py-12 text-red-600">{error}</div>
        </div>
        <ProjectForm
          mode={currentProject ? 'edit' : 'create'}
          initialData={currentProject || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleCancelForm}
          isOpen={isFormOpen}
          error={error}
          attachments={projectAttachments}
          onDeleteAttachment={(id) => setProjectAttachments((prev) => prev.filter((f) => f.id !== id))}
        />
      </SectionLayout>
    );
  }

  return (
    <SectionLayout title="Projects">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={handleAddProject}
            />
            <SearchButton
              onSearch={list.setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Generic Table */}
        <GenericTable
          columns={columns}
          {...list.tableProps}
          rowKey={(row) => row.id}
          scrollContainerMaxWidth="calc(100vw - 19.2rem)"
          minTableWidth={1200}
          onRowClick={handleRowClick}
          selectable={true}
          selectedKeys={selectedRows}
          onSelectionChange={setSelectedRows}
          emptyState="No projects found."
        />
      </div>

      <ProjectForm
        mode={currentProject ? 'edit' : 'create'}
        initialData={currentProject || undefined}
        onSubmit={handleFormSubmit}
        onCancel={handleCancelForm}
        isOpen={isFormOpen}
        error={error}
        attachments={projectAttachments}
        onDeleteAttachment={(id) => setProjectAttachments((prev) => prev.filter((f) => f.id !== id))}
      />
    </SectionLayout>
  );
}
